import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import equal from "fast-deep-equal";
import React, { useCallback, useMemo } from "react";

import { PlanId } from "components/common/boundary/PlanId";
import { CommentCell } from "components/common/plan/CommentCell";
import { DeployTable } from "components/common/plan/DeployTable";
import { DriMonthToDateConsumption } from "components/common/plan/DriMonthToDateConsumption";
import { LayerEditor } from "components/common/plan/layerEditor/LayerEditor";
import { renderMixPriceComparison } from "components/common/plan/MixSummaryCell";
import { SteelGradesSelect } from "components/common/plan/SteelGradesSelect";
import {
  makeRows,
  tableRowsToBackendMixes,
} from "components/common/plan/utils";
import { PlanAncestorsTooltip } from "components/common/planHistory/PlanAncestorsTooltip";
import { Row } from "components/common/plan/types";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation } from "hooks/formatters";
import { minimumPeriod, Period } from "hooks/periodIndex";
import { useSearchId } from "hooks/search";
import { useTenant } from "hooks/settings";
import { useIsMobile } from "hooks/useIsMobile";
import { useMixGrouping } from "hooks/useMixGrouping";
import { useMixCollapse } from "hooks/useMixCollapse";
import { useMixQuantity } from "hooks/useMixQuantity.tsx";
import { useLayerEditor } from "../exportPlan/useLayerEditor";
import {
  ChildMixUpdate,
  MaterialRead,
  PlannedResultsRead,
  SteelGrade,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  makePlanPeriodId,
  planMixTableEditsSlice,
  selectPlanMixTableEdits,
} from "src/store/slices/planMixesTableEditSlice";
import { selectSearchedSteelGradeIds } from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

interface Props {
  planResults: PlannedResultsRead;
  materials: MaterialRead[];
  steelGrades: SteelGrade[];
  tableMaterials: {
    id: number;
    name: string;
    inventory: number | null;
  }[];
  driInventory: number | null;
  timeString: string;
  setPlanId: (planId: PlanId) => void;
  toNextState: () => void;
  onSubmit: (updates: ChildMixUpdate[], comment: string) => Promise<void>;
}

export const LayerEditableApproveBody = ({
  planResults,
  materials,
  steelGrades,
  tableMaterials,
  driInventory,
  timeString,
  setPlanId,
  toNextState,
  onSubmit,
}: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const searchId = useSearchId();
  const isMobile = useIsMobile();
  const mixQuantity = useMixQuantity();
  const mixGrouping = useMixGrouping();
  const tenantName = useTenant();
  const { small_mass_per_mass, default_mix_breakdown, layer_edit_mode } =
    useTenantData();

  const planId = planResults.plan_id as PlanId;
  const period = minimumPeriod;
  const collapse = useMixCollapse(planId, period);

  const periodResults = useMemo(
    () => planResults.periods[period.toString()]?.mixes ?? [],
    [planResults, period]
  );

  const filteredSteelGrades = useMemo(() => {
    const steelGradeNames = new Set(
      periodResults.flatMap((result) =>
        result.production.map((item) => item.steel_grade_name)
      )
    );
    return steelGrades.filter((steelGrade) =>
      steelGradeNames.has(steelGrade.name)
    );
  }, [periodResults, steelGrades]);

  const planEdits = useAppSelector((state) =>
    selectPlanMixTableEdits(state, planId, period)
  );

  React.useEffect(() => {
    if (!planEdits) {
      dispatch(
        planMixTableEditsSlice.actions.set([
          planId,
          period,
          makeRows(planId, period, periodResults, steelGrades, mixQuantity),
        ])
      );
    }
  }, [
    planEdits,
    planId,
    period,
    periodResults,
    steelGrades,
    mixQuantity,
    dispatch,
  ]);

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, planId, period, "deploy")
  );

  const [comment, setComment] = React.useState("");

  const resultsByPlanPeriod = useMemo(
    () => ({ [makePlanPeriodId(planId, period)]: periodResults }),
    [planId, period, periodResults]
  );

  const {
    showingLayers,
    layerPreviewData,
    hasLayerEdits,
    mixIdsWithEdits,
    handleShowLayers,
    handleSave,
    handleDiscard,
    handleLayerStateChange,
    handleMixLayerStateChange,
    handleShowWeights,
    getLayerStateForMix,
    resetMixLayers,
  } = useLayerEditor({
    focussedPlanId: planId,
    focussedPeriod: period,
    formPlanId: planId,
    results: resultsByPlanPeriod,
    tenantName,
    searchId,
    setPlanId,
    comment,
    setComment,
    layerEditMode: layer_edit_mode,
  });

  const editedRowsForSummary = planEdits?.editedRows;
  const [doSummarisePlanResultsMutation] = useSummarisePlanResultsMutation({
    fixedCacheKey: makePlanPeriodId(planId, period),
  });

  React.useEffect(() => {
    if (!editedRowsForSummary) return;
    const mutation = doSummarisePlanResultsMutation({
      tenantName,
      unsummarisedPlanResults: {
        plan_id: planId,
        period,
        mixes: tableRowsToBackendMixes(editedRowsForSummary).map((mix) => ({
          ...mix,
          layer_source: mixIdsWithEdits.has(mix.original_mix_id)
            ? "manual"
            : "per_basket_totals",
        })),
      },
    });
    return () => {
      mutation.abort();
    };
  }, [
    doSummarisePlanResultsMutation,
    editedRowsForSummary,
    tenantName,
    planId,
    period,
    mixIdsWithEdits,
  ]);

  const handleOnTableChange = useCallback(
    (changedPlanId: PlanId, changedPeriod: Period, editedRows: Row[]) => {
      dispatch(
        planMixTableEditsSlice.actions.update([
          changedPlanId,
          changedPeriod,
          editedRows,
        ])
      );
    },
    [dispatch]
  );

  const handleSetMixDescription = useCallback(
    (mixId: number, description: string) => {
      dispatch(
        planMixTableEditsSlice.actions.setMixDescription([
          planId,
          period,
          mixId,
          description,
        ])
      );
    },
    [dispatch, planId, period]
  );

  const baseMassScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;

  const popoverLayerEditor = React.useMemo(
    () => ({
      baseMassScaleFactor,
      materials,
      getLayerStateForMix,
      onMixLayerStateChange: handleMixLayerStateChange,
      isMixManuallyLayered: (mixId: number) => mixIdsWithEdits.has(mixId),
      onResetMixLayers: resetMixLayers,
    }),
    [
      baseMassScaleFactor,
      materials,
      getLayerStateForMix,
      handleMixLayerStateChange,
      mixIdsWithEdits,
      resetMixLayers,
    ]
  );

  if (!planEdits) {
    return null;
  }

  const { isSubmitting, originalRows, editedRows } = planEdits;
  const hasWeightEdits = !equal(editedRows, originalRows);
  const hasAnyEdits = hasWeightEdits || hasLayerEdits;

  return (
    <Stack sx={{ gap: 1, maxHeight: "100%" }}>
      <Button
        sx={{ alignSelf: "end" }}
        onClick={toNextState}
        endIcon={<ArrowForwardIcon />}
        disabled={hasAnyEdits || isSubmitting}
      >
        {t("approveAndNext")}
      </Button>
      <Stack sx={{ flex: 1, minHeight: 0, maxHeight: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          my={2}
        >
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {timeString}
              </Typography>
              <Box>
                <Tooltip
                  title={<PlanAncestorsTooltip planId={planId} />}
                  placement="bottom"
                  arrow
                  enterDelay={100}
                  leaveDelay={500}
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          bgcolor: "background.paper",
                          color: "text.primary",
                          p: 0,
                          maxWidth: "none",
                        },
                      },
                    },
                  }}
                >
                  <IconButton size="small">
                    <HistoryRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
            {!showingLayers && mixQuantity === "mass" ? (
              <SteelGradesSelect
                view="deploy"
                planId={planId}
                period={period}
                steelGrades={filteredSteelGrades}
              />
            ) : null}
          </Stack>
          {driInventory !== null ? (
            <DriMonthToDateConsumption
              planId={planId}
              period={period}
              materials={materials}
            />
          ) : (
            <Box />
          )}
          <Stack spacing={1}>
            <CommentCell onCommentChange={setComment} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <ToggleButtonGroup
                size="small"
                value={showingLayers ? "layers" : "mixes"}
                exclusive
                disabled={isSubmitting}
                onChange={(_, value: "mixes" | "layers" | null) => {
                  if (value === "mixes") handleShowWeights();
                  else if (value === "layers") handleShowLayers();
                }}
              >
                <ToggleButton value="mixes">{t("mixes")}</ToggleButton>
                <ToggleButton value="layers">{t("layers")}</ToggleButton>
              </ToggleButtonGroup>
              <Button
                disabled={!hasAnyEdits || isSubmitting}
                onClick={handleDiscard}
                color="primary"
                variant="text"
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("discardChanges")}
              </Button>
              <LoadingButton
                loading={isSubmitting}
                disabled={!hasAnyEdits}
                onClick={() => void handleSave()}
                color="primary"
                variant="contained"
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("saveChanges")}
              </LoadingButton>
            </Box>
          </Stack>
        </Stack>
        <Stack sx={{ flex: 1, overflow: "auto" }}>
          {layerPreviewData ? (
            <Box
              sx={{
                display: showingLayers ? "contents" : "none",
              }}
            >
              <LayerEditor
                initialState={layerPreviewData}
                renderMixSummary={(mixId) =>
                  renderMixPriceComparison({
                    planId,
                    period,
                    mixId,
                    originalMixes: periodResults,
                  })
                }
                materials={materials}
                onStateChange={handleLayerStateChange}
                weightsEditable
                massScaleFactor={baseMassScaleFactor}
                manuallyLayeredMixIds={mixIdsWithEdits}
                onResetMixLayers={resetMixLayers}
                collapsedMixIds={collapse.collapsedMixIds}
                onToggleCollapse={collapse.onToggleCollapse}
              />
            </Box>
          ) : showingLayers ? (
            <Skeleton variant="rectangular" height={400} animation="wave" />
          ) : null}
          <Box sx={{ display: showingLayers ? "none" : "contents" }}>
            <DeployTable
              planId={planId}
              period={period}
              disableEdit={isSubmitting}
              hasEdited={hasWeightEdits}
              rows={editedRows}
              materials={tableMaterials}
              selectedSteelGradeIds={selectedSteelGradeIds}
              onChange={handleOnTableChange}
              mixGrouping={mixGrouping}
              driInventory={driInventory}
              steelGradesWidth={isMobile ? 120 : 210}
              onSubmit={onSubmit}
              setMixDescription={handleSetMixDescription}
              popoverLayerEditor={popoverLayerEditor}
              collapse={collapse}
            />
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};
